/**
 * The H3 claim grid: mounts and disposes `hexOverlay`, sources the height its
 * drape samples, builds the per-frame `HexOverlayView`, builds and paints the
 * quad outlines, and holds the on/off toggle plus the view-layers and
 * warp-copy switches the overlay panel and the warp row read and write.
 */

import * as THREE from 'three';

import {
  createHexOverlay, hexGroundHeightAt,
  type HexGroundPass, type HexGrounds, type HexOverlayHandle, type HexOverlayView,
} from './hexOverlay.ts';
import { latLonToVec3 } from './latLonToVec3.ts';
import { directionForLatLon } from './tileTree.ts';
import { shapeHeightAt } from './shapeGlobe.ts';
import type { PickHandle } from './pick';
import type { PyramidViewHandle } from './pyramidView';
import type { SunriseWarpHandle } from './sunriseWarp';
import type { ViewLayersHandle } from './viewLayers';
import type { WarpRowHandle, WarpRowLevel } from './warpRow';
import type { FocusRigHandle } from './focusRig';
import type { MountHandle } from './mount';
import type { BodyReadersHandle } from './bodyReaders';
import type { DescentAnchorHandle } from './descentAnchor';
import type { SurfaceRadiusHandle } from './surfaceRadius';
import type { PlotGeometryHandle } from './plotGeometry';
import type { BodySceneHandle } from './bodyScene';
import type { InjectsHandle } from './injects';
import type { CameraRigHandle } from './cameraRig';
import type { NavigationHandle } from './navigation';
import type { FarFieldHandle } from './farField';
import type { AtlasLive, AtlasState } from '../atlas';
import type { Quadrangle } from '../world/types';
import { TEST_SWITCHES } from '../urlFlags.ts';

export interface HexViewDeps {
  readonly focusRig: FocusRigHandle;
  readonly mount: MountHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly plotGeometry: PlotGeometryHandle;
  readonly bodyScene: BodySceneHandle;
  readonly injects: InjectsHandle;
  readonly live: AtlasLive;
  readonly pyramidView: PyramidViewHandle;
  /** Built after this module, so atlas.ts hands these over through getters. */
  readonly cameraRig: CameraRigHandle;
  readonly navigation: NavigationHandle;
  readonly farField: FarFieldHandle;
  readonly pick: PickHandle;
  readonly _sunFixed: THREE.Vector3;
  readonly detailGroup: THREE.Group;
  own(fn: () => void): void;
  readonly root: HTMLElement;
  readonly state: AtlasState;
}

export interface HexViewHandle {
  readonly HEX_KEY: string;
  readonly WARP_COPY_LEVELS: readonly WarpRowLevel[];
  applyQuadAvailability(): void;
  buildQuadLines(): void;
  cameraGroundPoint(): { lat: number; lon: number; radiusM: number };
  cellCrumb(cell: string): string;
  hexHeightAt(latDeg: number, lonDeg: number): number | null;
  readonly hexOverlay: HexOverlayHandle;
  hexView(): HexOverlayView;
  readonly quadLines: THREE.Group;
  setHexesEnabled(on: boolean): void;
  syncHexOverlay(): void;
  syncHexVisibility(): void;
  hexesEnabled: boolean;
  layerInert: boolean;
  layerTickPaintOff: boolean;
  viewLayers: ViewLayersHandle | null;
  warpCopy: boolean;
  warpCopyPick: number;
  warpRow: WarpRowHandle | null;
  sunrise: SunriseWarpHandle | null;
}

/**
 * How far off the datum the quadrangle outlines float, as a fraction of the
 * radius.
 *
 * Two kilometres and up on Mars. Metres would z-fight at ORBIT, and the old
 * fractions - 0.5% and 1% of the radius, 17 and 34 km - put an outline above
 * the camera once LOCAL became a real altitude rather than a tier. The hex
 * grid has no lift of its own any more: it drapes on the ground
 * (`src/atlas/hexOverlay.ts`).
 */
const QUAD_LIFT = 1.0006;

export function createHexView(deps: HexViewDeps): HexViewHandle {
  const { _sunFixed, detailGroup, own, pyramidView, root, state } = deps;
  const { altitude, focusCam } = deps.focusRig;
  const { camera } = deps.mount;
  const { bodyQuads, descendable, ladderBody, registryDatumM, rowById } = deps.bodyReaders;
  const { anchorLatLon, availableQuad } = deps.descentAnchorHandle;
  const { groundDatumM } = deps.surfaceRadius;
  const { regionXZAt } = deps.plotGeometry;

  // The map quadrangles a body publishes are registry data (`quadrangles` on the
  // body row): Mars carries the USGS MC-01..MC-30 series in its real layout (a cap, rows of 6, 8, 8 and 6, a cap),
  // most bodies carry none, and a body without the field draws no outlines. Which
  // quad a descent is open in is not a flag: it is whichever one contains the
  // surveyed site's own centre (`availableQuad`).
  //
  // Built from the body row rather than at construction: the table is registry
  // data now, so there is nothing to draw until the fetch lands, and the set
  // changes with the body.
  const quadLines = new THREE.Group();
  quadLines.visible = false;
  // Must precede `hexOverlay.object` below or the equal-renderOrder draw order moves.
  detailGroup.add(quadLines);
  own(() => clearQuadLines());

  function clearQuadLines(): void {
    for (const child of quadLines.children) {
      const line = child as THREE.LineSegments;
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    }
    quadLines.clear();
  }

  function buildQuadLines(): void {
    clearQuadLines();
    const radius = registryDatumM(ladderBody());
    if (radius === 0) return;
    for (const quad of bodyQuads()) {
      const segs = buildQuadOutline(quad, radius * QUAD_LIFT);
      const mat = new THREE.LineBasicMaterial({ transparent: true });
      const line = new THREE.LineSegments(segs, mat);
      line.userData['quadId'] = quad.id;
      quadLines.add(line);
    }
  }

  // The H3 claim grid. Its resolution is the focused row's own
  // `default_h3_resolution`, aggregated up by altitude; `src/atlas/hexOverlay.ts`
  // owns the derivation and this file only feeds it the view.
  const hexOverlay = createHexOverlay({
    // The build is sliced across frames, so the frame it lands on is not
    // always one the camera was already going to redraw.
    onBuild: () => { deps.live.needsRender = true; },
  });
  hexOverlay.object.visible = false;
  detailGroup.add(hexOverlay.object);
  own(() => {
    detailGroup.remove(hexOverlay.object);
    hexOverlay.dispose();
  });

  // Whether the grid draws at all, independent of the ground/resolution gate
  // below; the toggle never disposes or rebuilds the overlay itself.
  const HEX_KEY = 'atlas.hexes.v1';
  /**
   * `descent-dojo --layers --inject hex-default-on`: force a fresh profile's
   * default back on, the regression the layers arm's hex-pref bar exists to
   * catch.
   */
  const hexDefaultOnInject = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('hexdefaulton') === '1';
  // A player who has never touched this switch gets the grid off; a stored
  // preference below still wins either way.
  let hexesEnabled = hexDefaultOnInject;
  let viewLayers: ViewLayersHandle | null = null;
  /** `descent-dojo --layers --inject layer-inert`: a switch that reaches nothing. */
  let layerInert = false;
  /** `--inject row-paint-off`: the control on the tick's own repaint of the panel, which nothing a player reaches sets. */
  let layerTickPaintOff = false;
  let warpRow: WarpRowHandle | null = null;
  let sunrise: SunriseWarpHandle | null = null;
  /**
   * `descent-dojo --viewwarp --inject viewwarp-copy`: the row keeps its OWN
   * picked level and its clicks never reach the sim, which is the second copy
   * this row exists not to be.
   */
  let warpCopy = false;
  let warpCopyPick = 0;
  const WARP_COPY_LEVELS = [
    { rate: 1, label: 'REAL TIME', short: 'REAL' },
    { rate: 60, label: '1 MIN/S', short: 'MIN' },
    { rate: 3600, label: '1 HR/S', short: 'HR' },
    { rate: 86400, label: '1 DAY/S', short: 'DAY' },
  ] as const;
  try {
    const stored = localStorage.getItem(HEX_KEY);
    if (stored !== null) hexesEnabled = stored !== '0';
  } catch { /* private mode */ }

  // `deps.farField.farFieldDrawn` is read through a getter here too: `drawFrame` in
  // `src/atlas.ts` only sets it once a plot is up at SURFACE, long after this
  // module is constructed.
  const hexPass: HexGroundPass = {
    get surface() { return deps.cameraRig.surfaceRegime(); },
    get plot() { return deps.live.plot !== null; },
    get farField() { return deps.farField.farFieldDrawn; },
  };
  const hexGrounds: HexGrounds = {
    field: plotFieldHeightAt,
    leaves: (latDeg, lonDeg) => pyramidView.tileMeshHeightAt(latDeg, lonDeg),
    globe: globeHeightAt,
  };

  /** Height source for the hex overlay's own draping: the ground drawn with the grid. */
  function hexHeightAt(latDeg: number, lonDeg: number): number | null {
    return hexGroundHeightAt(latDeg, lonDeg, hexPass, hexGrounds);
  }

  /**
   * The plot field's height, null outside its footprint: `heightAt` clamps to
   * its edge texel rather than refusing an out-of-range sample.
   */
  function plotFieldHeightAt(latDeg: number, lonDeg: number): number | null {
    const field = deps.live.plotField;
    if (!field) return null;
    const m = field.meta;
    const xz = regionXZAt(latDeg, lonDeg);
    if (!xz || Math.abs(xz.x) > m.bbox_m.width / 2 || Math.abs(xz.z) > m.bbox_m.height / 2) return null;
    return field.heightAt(xz.x, xz.z);
  }

  /**
   * Elevation under a lat/lon off the DISPLACED GLOBE, where that globe is the
   * ground that writes depth.
   *
   * With no pyramid drawn the globe is not an underlay: it keeps its own depth
   * rather than the quarter-radius push `setGlobeUnderlay` gives it, so relief
   * on it cuts anything draped under it. A grid left on the datum lost 7.4% of
   * its line on Mars at ORBIT and 46.7% on Ceres. Where the pyramid IS drawn
   * the push holds the globe out of the way and a point with no leaf over it
   * keeps the datum float, which is what every band below ORBIT reads.
   */
  function globeHeightAt(latDeg: number, lonDeg: number): number | null {
    if (deps.injects.state.datumDrapeDebug || pyramidView.globeUnderlay || deps.bodyScene.state.shapeField === null) return null;
    const id = ladderBody();
    const unitR = rowById(id)?.radius_m ?? null;
    if (unitR === null) return null;
    const dir = directionForLatLon((latDeg * Math.PI) / 180, (lonDeg * Math.PI) / 180);
    const h = shapeHeightAt(deps.bodyScene.state.shapeField, dir);
    if (!Number.isFinite(h)) return null;
    return unitR * (1 + h / deps.bodyScene.state.shapeField.datumRadiusM) - groundDatumM(id);
  }

  /**
   * The sub-camera point and the eye's distance from the body centre.
   *
   * Body-fixed, through the same rotation `latLonOfHit` reads a click through:
   * the overlay centres and sizes its cap on the ground under the EYE, which
   * at every band's own shallow pitch is tens of degrees of arc from the
   * focus the camera is pointed at.
   */
  function cameraGroundPoint(): { lat: number; lon: number; radiusM: number } {
    const node = deps.bodyScene.state.bodyNode;
    if (!node) return { lat: 0, lon: 0, radiusM: groundDatumM(ladderBody()) };
    // The camera's SCENE position, which is the frame `latLonOfHit` reads a
    // click's hit point in. `cameraWorld` and `node.world` are the other frame
    // - correct for a distance, and 100 degrees of arc out as a direction.
    const ll = deps.pick.latLonOfHit(camera.position);
    return { ...ll, radiusM: camera.position.distanceTo(node.mesh.position) };
  }

  /** What the overlay needs to know, in one place, from this frame. */
  function hexView(): HexOverlayView {
    const id = ladderBody();
    const row = rowById(id);
    const a = anchorLatLon();
    const centre = focusCam.state.latDeg !== null && focusCam.state.lonDeg !== null
      ? { lat: focusCam.state.latDeg, lon: focusCam.state.lonDeg }
      : a;
    const h = Math.max(1, root.clientHeight);
    const eye = cameraGroundPoint();
    const e = row?.ellipsoid;
    return {
      baseRes: row?.default_h3_resolution ?? null,
      radiusM: groundDatumM(id),
      ellipsoidAxesM: e ? [e.a_m, e.b_m, e.c_m] : null,
      latDeg: centre?.lat ?? 0,
      lonDeg: centre?.lon ?? 0,
      camLatDeg: eye.lat,
      camLonDeg: eye.lon,
      camRadiusM: eye.radiusM,
      altitudeM: altitude(),
      viewportH: h,
      fovDeg: camera.fov,
      aspect: Math.max(1, root.clientWidth) / h,
      anchor: a,
      heightAt: hexHeightAt,
      heightVersion: pyramidView.tileHeightVersion,
      sunDirFixed: _sunFixed,
    };
  }

  /**
   * A drawn cell in the breadcrumb.
   *
   * The H3 index short form, not a three-word name: the MoM codec is a
   * bijection over resolution 8 alone (`src/atlas/momName.ts`) and the drawn
   * cell is whichever resolution the altitude selected, usually coarser.
   */
  function cellCrumb(cell: string): string {
    return `Cell ${cell.slice(0, 8)}`;
  }

  /**
   * Whether the grid is drawn at all, which is an answer only the registry
   * has: the rows land after the mount's own `applyState`, so a state entered
   * by URL keeps the pre-registry answer - no grid, and so no cell - unless
   * something re-derives it once they are in.
   */
  function syncHexVisibility(): void {
    hexOverlay.object.visible = descendable()
      && (rowById(state.bodyId)?.default_h3_resolution ?? null) !== null
      && hexesEnabled;
  }

  function syncHexOverlay(): void {
    if (deps.live.disposed) return;
    const view = hexView();
    hexOverlay.update(view);
    // `syncTierToAltitude` leaves `state.cellId` null on a body reaching the
    // hex tiers for the first time rather than reading `anchorCell()` there
    // directly: the overlay's own build is time-sliced across frames
    // (`BUILD_BUDGET_MS`), so its drawn resolution can still be the PREVIOUS
    // rung for several frames after the altitude that wants a new one, and an
    // anchor read in that window is a stale, coarser cell that never gets
    // corrected once frozen. Reading it here, once the drawn resolution has
    // actually caught up to what this view derives, is what makes the first
    // card a player sees the same cell the grid is actually highlighting.
    if (state.tier >= 3 && state.cellId === null && descendable()
      && hexOverlay.debug().drawnRes === hexOverlay.resolutionAt(view)) {
      state.cellId = hexOverlay.anchorCell();
    }
  }

  /**
   * Paint the quad outlines. Which one is open is derived, not stored, so the
   * cyan quad cannot drift from the ground the descent actually reaches.
   */
  function applyQuadAvailability(): void {
    const openId = availableQuad()?.id ?? null;
    for (const child of quadLines.children) {
      const line = child as THREE.LineSegments;
      const open = line.userData['quadId'] === openId;
      line.userData['available'] = open;
      const mat = line.material as THREE.LineBasicMaterial;
      mat.color.setHex(open ? 0x66ffff : 0x88aabb);
      mat.opacity = open ? 0.95 : 0.35;
      mat.userData['baseOpacity'] = mat.opacity;
    }
  }

  function setHexesEnabled(on: boolean): void {
    hexesEnabled = on;
    try { localStorage.setItem(HEX_KEY, on ? '1' : '0'); } catch { /* private mode */ }
    viewLayers?.paint();
    if (!layerInert) deps.navigation.applyState({ reframe: false });
  }

  return {
    HEX_KEY,
    WARP_COPY_LEVELS,
    applyQuadAvailability,
    buildQuadLines,
    cameraGroundPoint,
    cellCrumb,
    hexHeightAt,
    hexOverlay,
    hexView,
    quadLines,
    setHexesEnabled,
    syncHexOverlay,
    syncHexVisibility,
    get hexesEnabled() { return hexesEnabled; },
    set hexesEnabled(v) { hexesEnabled = v; },
    get layerInert() { return layerInert; },
    set layerInert(v) { layerInert = v; },
    get layerTickPaintOff() { return layerTickPaintOff; },
    set layerTickPaintOff(v) { layerTickPaintOff = v; },
    get viewLayers() { return viewLayers; },
    set viewLayers(v) { viewLayers = v; },
    get warpCopy() { return warpCopy; },
    set warpCopy(v) { warpCopy = v; },
    get warpCopyPick() { return warpCopyPick; },
    set warpCopyPick(v) { warpCopyPick = v; },
    get warpRow() { return warpRow; },
    set warpRow(v) { warpRow = v; },
    get sunrise() { return sunrise; },
    set sunrise(v) { sunrise = v; },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Geometry builders
// ─────────────────────────────────────────────────────────────────────────────

function buildQuadOutline(quad: Quadrangle, radius: number): THREE.BufferGeometry {
  // Draw the 4 edges of the quadrangle as line segments, sampling several
  // points along each edge so curvature follows the sphere.
  const samplesPerEdge = 16;
  const pts: number[] = [];
  function pushEdge(latA: number, lonA: number, latB: number, lonB: number): void {
    for (let i = 0; i < samplesPerEdge; i++) {
      const t0 = i / samplesPerEdge;
      const t1 = (i + 1) / samplesPerEdge;
      const a = latLonToVec3(latA + (latB - latA) * t0, lonA + (lonB - lonA) * t0, radius);
      const b = latLonToVec3(latA + (latB - latA) * t1, lonA + (lonB - lonA) * t1, radius);
      pts.push(a.x, a.y, a.z, b.x, b.y, b.z);
    }
  }
  pushEdge(quad.latMin, quad.lonMin, quad.latMin, quad.lonMax);
  pushEdge(quad.latMin, quad.lonMax, quad.latMax, quad.lonMax);
  pushEdge(quad.latMax, quad.lonMax, quad.latMax, quad.lonMin);
  pushEdge(quad.latMax, quad.lonMin, quad.latMin, quad.lonMin);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
  return geo;
}
